import { useState } from 'react';
import { Skeleton } from '@monksflow/monks-ui';
import { ImageOff } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useGetSignedUrl } from '@/shared/hooks/useGetSignedUrl';

interface ThumbnailImage {
  assetId: string;
  metadata?: { fileName?: string };
}

export interface ImageReplyThumbnailProps {
  images: ThumbnailImage[];
}

/**
 * The first image a reply generated, small, in the place its card shows an icon; the card opens
 * the widget where the images are large. The URL is fetched per asset because signed links
 * expire, so none is stored with the reply. A plain signed link the browser loads lazily, not the
 * cached download the widget uses: a long chat would otherwise fetch every full image on open.
 */
export function ImageReplyThumbnail({ images }: ImageReplyThumbnailProps) {
  const { t } = useTranslation('chats');
  const first = images.at(0);
  const { downloadUrl, isLoading, isError } = useGetSignedUrl(first?.assetId);
  const [brokenAssetId, setBrokenAssetId] = useState<string>();
  if (!first) return null;
  return (
    <div className="relative size-12 shrink-0 overflow-hidden rounded-lg border border-border bg-muted">
      {isError || brokenAssetId === first.assetId ? (
        <div
          role="img"
          aria-label={t('chats.chatWidgetToggle.imageUnavailable')}
          className="flex size-full items-center justify-center text-muted-foreground"
        >
          <ImageOff className="size-5" aria-hidden />
        </div>
      ) : isLoading || !downloadUrl ? (
        <Skeleton className="size-full" />
      ) : (
        <img
          src={downloadUrl}
          loading="lazy"
          alt={first.metadata?.fileName ?? ''}
          className="size-full object-cover"
          onError={() => {
            setBrokenAssetId(first.assetId);
          }}
        />
      )}
      {images.length > 1 && (
        <span className="absolute bottom-0.5 right-0.5 rounded bg-background/80 px-1 text-[10px] font-medium text-foreground">
          {t('chats.chatWidgetToggle.moreImages', { count: images.length - 1 })}
        </span>
      )}
    </div>
  );
}
